"use client";

import { useForm, useWatch } from "react-hook-form";
import Link from "next/link";
import {
  HiSearch,
  HiExternalLink,
  HiClipboardCopy,
  HiFilter,
  HiCalendar,
  HiUser,
  HiQuestionMarkCircle,
} from "@/components/icons";
import { FaXTwitter } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

import { useToast } from "@/hooks/use-toast";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import { xAdvancedSearchFAQ } from "@/lib/data/faq/x-advanced-search";

type TwitterSearchFormValues = {
  // Words
  allWords: string;
  exactPhrase: string;
  anyWords: string;
  noneWords: string;
  hashtags: string;
  language: string;

  // Accounts
  fromAccounts: string;
  toAccounts: string;
  mentionAccounts: string;

  // Filters
  replies: "include" | "exclude" | "only";
  links: "include" | "exclude" | "only";
  media: "all" | "images" | "videos" | "native_video";

  // Engagement
  minReplies: string;
  minFaves: string;
  minRetweets: string;

  // Dates
  since: string;
  until: string;
};

export default function XAdvancedSearchClient() {
  const { register, control } = useForm<TwitterSearchFormValues>({
    defaultValues: {
      replies: "include",
      links: "include",
      media: "all",
    },
  });

  const values = useWatch({ control });
  const { toast } = useToast();

  const queryPreview = (() => {
    // Logic duplicated for live preview
    const queryParts: string[] = [];
    if (values.allWords) queryParts.push(values.allWords);
    if (values.exactPhrase) queryParts.push(`"${values.exactPhrase}"`);
    if (values.anyWords) queryParts.push(`(${values.anyWords.split(" ").join(" OR ")})`);
    if (values.noneWords)
      queryParts.push(
        values.noneWords
          .split(" ")
          .map((w) => `-${w}`)
          .join(" ")
      );
    if (values.hashtags)
      queryParts.push(
        values.hashtags
          .split(" ")
          .map((h) => (h.startsWith("#") ? h : `#${h}`))
          .join(" ")
      );
    if (values.language && values.language !== "any") queryParts.push(`lang:${values.language}`);
    if (values.fromAccounts)
      queryParts.push(
        values.fromAccounts
          .split(" ")
          .map((a) => `from:${a.replace("@", "")}`)
          .join(" ")
      );
    if (values.toAccounts)
      queryParts.push(
        values.toAccounts
          .split(" ")
          .map((a) => `to:${a.replace("@", "")}`)
          .join(" ")
      );
    if (values.mentionAccounts)
      queryParts.push(
        values.mentionAccounts
          .split(" ")
          .map((a) => `@${a.replace("@", "")}`)
          .join(" ")
      );
    if (values.replies === "exclude") queryParts.push("-filter:replies");
    else if (values.replies === "only") queryParts.push("filter:replies");
    if (values.links === "exclude") queryParts.push("-filter:links");
    else if (values.links === "only") queryParts.push("filter:links");
    if (values.media === "images") queryParts.push("filter:images");
    else if (values.media === "videos") queryParts.push("filter:videos");
    else if (values.media === "native_video") queryParts.push("filter:native_video");
    if (values.minReplies) queryParts.push(`min_replies:${values.minReplies}`);
    if (values.minFaves) queryParts.push(`min_faves:${values.minFaves}`);
    if (values.minRetweets) queryParts.push(`min_retweets:${values.minRetweets}`);
    if (values.since) queryParts.push(`since:${values.since}`);
    if (values.until) queryParts.push(`until:${values.until}`);
    return queryParts.join(" ");
  })();

  const fullUrl = `https://twitter.com/search?q=${encodeURIComponent(queryPreview)}&src=typed_query`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(queryPreview);
    toast({
      title: "Copied to clipboard",
      description: "Search query copied to clipboard.",
    });
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="X (Twitter) Advanced Search Generator"
        description="Build complex X (Twitter) search queries without memorizing operators. Filter by dates, engagement, accounts, hashtags, language, and more."
        url="https://plugmonkey.xyz/tool/x-advanced-search/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          { name: "X Advanced Search", url: "https://plugmonkey.xyz/tool/x-advanced-search/" },
        ]}
        faq={xAdvancedSearchFAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-black rounded-full text-white">
          <FaXTwitter className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">X Advanced Search Generator</h1>
          <p className="text-muted-foreground mt-1">
            Build complex X (Twitter) search queries without memorizing operators.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          X's (Twitter) search is powerful, but building complex queries requires memorizing
          operators like 'from:', 'filter:', 'min_replies:', and more. This tool makes it
          simple—build sophisticated searches with filters for accounts, dates, engagement metrics,
          hashtags, language, and content types, all through a clean visual interface.
        </p>
        <p>
          Perfect for researchers, marketers, and anyone who needs to find specific tweets quickly.
          Filter by engagement to find viral content, search within date ranges for event-related
          tweets, or combine multiple filters to uncover exactly what you're looking for. Generate
          your search query, copy it, or jump straight to X with your filters applied.
        </p>
        <p>
          No more guessing at search syntax. Build your query visually, see exactly what you're
          searching for, and get better results faster.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiSearch /> Words
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="xas-allWords">All of these words</Label>
                  <Input
                    id="xas-allWords"
                    {...register("allWords")}
                    placeholder="social media marketing"
                    suppressHydrationWarning
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="xas-exactPhrase">This exact phrase</Label>
                  <Input
                    id="xas-exactPhrase"
                    {...register("exactPhrase")}
                    placeholder="growth hacking"
                    suppressHydrationWarning
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="xas-anyWords">Any of these words (OR)</Label>
                  <Input
                    id="xas-anyWords"
                    {...register("anyWords")}
                    placeholder="twitter facebook instagram"
                    suppressHydrationWarning
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="xas-noneWords">None of these words (-)</Label>
                  <Input
                    id="xas-noneWords"
                    {...register("noneWords")}
                    placeholder="spam bot"
                    suppressHydrationWarning
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="xas-hashtags">These hashtags (#)</Label>
                  <Input
                    id="xas-hashtags"
                    {...register("hashtags")}
                    placeholder="marketing tips"
                    suppressHydrationWarning
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="xas-language">Language</Label>
                  <select
                    id="xas-language"
                    {...register("language")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="any">Any Language</option>
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="ja">Japanese</option>
                    <option value="ar">Arabic</option>
                    <option value="pt">Portuguese</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiUser /> Accounts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="xas-fromAccounts">From these accounts</Label>
                  <Input
                    id="xas-fromAccounts"
                    {...register("fromAccounts")}
                    placeholder="@elonmusk"
                    suppressHydrationWarning
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="xas-toAccounts">To these accounts</Label>
                  <Input
                    id="xas-toAccounts"
                    {...register("toAccounts")}
                    placeholder="@plugmonkey"
                    suppressHydrationWarning
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="xas-mentionAccounts">Mentioning these accounts</Label>
                  <Input
                    id="xas-mentionAccounts"
                    {...register("mentionAccounts")}
                    placeholder="@verge"
                    suppressHydrationWarning
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiFilter /> Filters & Engagement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-3">
                  {/* Group headings, not <label>s: each names the radiogroup below
                      via aria-labelledby, and a <label> with no field of its own
                      is flagged by Chrome. */}
                  <span id="xas-replies-label" className="text-sm font-medium leading-none">
                    Replies
                  </span>
                  <div
                    className="flex flex-col gap-2"
                    role="radiogroup"
                    aria-labelledby="xas-replies-label"
                  >
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        value="include"
                        {...register("replies")}
                        className="accent-primary"
                        suppressHydrationWarning
                      />{" "}
                      Include replies
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        value="exclude"
                        {...register("replies")}
                        className="accent-primary"
                        suppressHydrationWarning
                      />{" "}
                      Exclude replies
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        value="only"
                        {...register("replies")}
                        className="accent-primary"
                        suppressHydrationWarning
                      />{" "}
                      Only replies
                    </label>
                  </div>
                </div>

                <div className="space-y-3">
                  <span id="xas-links-label" className="text-sm font-medium leading-none">
                    Links
                  </span>
                  <div
                    className="flex flex-col gap-2"
                    role="radiogroup"
                    aria-labelledby="xas-links-label"
                  >
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        value="include"
                        {...register("links")}
                        className="accent-primary"
                        suppressHydrationWarning
                      />{" "}
                      Include links
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        value="exclude"
                        {...register("links")}
                        className="accent-primary"
                        suppressHydrationWarning
                      />{" "}
                      Exclude links
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        value="only"
                        {...register("links")}
                        className="accent-primary"
                        suppressHydrationWarning
                      />{" "}
                      Only links
                    </label>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="xas-media">Media</Label>
                  <select
                    id="xas-media"
                    {...register("media")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="all">All Media</option>
                    <option value="images">Images Only</option>
                    <option value="videos">Videos Only</option>
                    <option value="native_video">Native Video Only</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-border/50">
                <div className="space-y-2">
                  <Label htmlFor="xas-minReplies">Min Replies</Label>
                  <Input
                    id="xas-minReplies"
                    type="number"
                    {...register("minReplies")}
                    placeholder="10"
                    suppressHydrationWarning
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="xas-minFaves">Min Likes (Faves)</Label>
                  <Input
                    id="xas-minFaves"
                    type="number"
                    {...register("minFaves")}
                    placeholder="100"
                    suppressHydrationWarning
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="xas-minRetweets">Min Retweets</Label>
                  <Input
                    id="xas-minRetweets"
                    type="number"
                    {...register("minRetweets")}
                    placeholder="50"
                    suppressHydrationWarning
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiCalendar /> Dates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="xas-since">Since (YYYY-MM-DD)</Label>
                  <Input
                    id="xas-since"
                    type="date"
                    {...register("since")}
                    suppressHydrationWarning
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="xas-until">Until (YYYY-MM-DD)</Label>
                  <Input
                    id="xas-until"
                    type="date"
                    {...register("until")}
                    suppressHydrationWarning
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <Card className="bg-muted/50 border-primary/20 shadow-lg">
              <CardHeader>
                <CardTitle>Generated Query</CardTitle>
                <CardDescription>Use this query directly in X search</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-background border rounded-md p-4 font-mono text-sm break-words min-h-[60px]">
                  {queryPreview || (
                    <span className="text-muted-foreground opacity-50">
                      Start typing to generate query...
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="w-full"
                    disabled={!queryPreview}
                  >
                    <HiClipboardCopy className="mr-2 h-4 w-4" /> Copy Query
                  </Button>
                  <Button
                    onClick={() => window.open(fullUrl, "_blank")}
                    className="w-full bg-black hover:bg-neutral-800 text-white"
                    disabled={!queryPreview}
                  >
                    Search on <FaXTwitter className="mr-2 h-4 w-4" />
                    <HiExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h2 className="font-bold text-lg mb-4">Pro Tip: Clean Your Feed</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Advanced search helps you find things, but what about removing the noise? Use our{" "}
                <strong>Unfollow Pro for X</strong> extension to mass-unfollow inactive accounts.
              </p>
              <Button asChild variant="secondary" className="w-full">
                <Link href="/product/x-unfollow-pro/">View Extension</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ
              items={xAdvancedSearchFAQ.map((item) => ({
                question: item.question,
                answer: item.answer,
              }))}
              showSupportLink={false}
              className="space-y-4"
            />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
